import { useSearchParams } from 'react-router';
import { useEffect, useMemo, useState } from 'react';
import { useChannelsStore } from '@/store/useChannelsStore';
import { usePlayerStore } from '@/store/usePlayerStore';
import { ChannelGrid } from '@/components/channel/ChannelGrid';
import { PageHeader } from '@/components/common/PageHeader';
import { searchChannels } from '@/lib/channels';
import { Search, Loader2, Globe, X } from 'lucide-react';

export function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('q') || '';

  const {
    sportsIndex,
    sportsChannels,
    allIndex,
    allChannels,
    isLoadingAll,
    isLoadingInitial,
    loadAll,
  } = useChannelsStore();
  const setCurrentChannel = usePlayerStore((s) => s.setCurrentChannel);
  const currentChannel = usePlayerStore((s) => s.currentChannel);

  const [inputValue, setInputValue] = useState(query);

  useEffect(() => {
    setInputValue(query);
  }, [query]);

  const sportsResults = useMemo(() => {
    if (!query.trim() || !sportsIndex) return [];
    return searchChannels(sportsIndex, sportsChannels, query, 50);
  }, [query, sportsIndex, sportsChannels]);

  const allResults = useMemo(() => {
    if (!query.trim() || !allIndex || !allChannels) return [];
    return searchChannels(allIndex, allChannels, query, 50);
  }, [query, allIndex, allChannels]);

  const showSearchAllBanner =
    query.trim().length > 0 && sportsResults.length < 3 && !allChannels;

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (inputValue.trim()) {
      setSearchParams({ q: inputValue.trim() });
    }
  };

  const handleClear = () => {
    setInputValue('');
    setSearchParams({});
  };

  if (isLoadingInitial) {
    return (
      <div className="flex items-center justify-center py-20 text-text-muted">
        <Loader2 className="w-6 h-6 animate-spin mr-2" />
        Loading search index...
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 h-full">
      <div>
        <PageHeader
          icon={<Search className="w-7 h-7 text-text-muted" />}
          title="Search"
        />
        <form onSubmit={handleSearch} className="relative max-w-2xl mt-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted pointer-events-none" />
          <input
            type="search"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder="Search channels by name, country, or category..."
            className="w-full h-11 pl-10 pr-10 bg-bg-elevated border border-border rounded-lg text-text placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent"
            autoFocus
          />
          {inputValue && (
            <button
              type="button"
              onClick={handleClear}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-text-muted hover:text-text"
              aria-label="Clear search"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </form>
      </div>

      {!query.trim() ? (
        <div className="text-center py-12 text-text-muted">
          <Search className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>Type a search term above to find channels.</p>
          <p className="text-xs mt-1 opacity-60">
            Searches {sportsChannels.length.toLocaleString()} sports channels by default.
          </p>
        </div>
      ) : (
        <>
          {/* Search-all banner */}
          {showSearchAllBanner && (
            <div className="bg-accent/10 border border-accent/30 rounded-lg p-4 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <Globe className="w-5 h-5 text-accent flex-shrink-0" />
                <div>
                  <p className="font-medium text-sm">No sports matches for "{query}"</p>
                  <p className="text-xs text-text-muted">
                    Search all 10,000+ channels including non-sports broadcasters.
                  </p>
                </div>
              </div>
              <button
                onClick={loadAll}
                disabled={isLoadingAll}
                className="px-4 py-2 bg-accent text-white rounded-lg text-sm font-medium hover:bg-accent-hover transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {isLoadingAll ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading...
                  </>
                ) : (
                  <>Search all 10k+ channels</>
                )}
              </button>
            </div>
          )}

          {/* Sports results */}
          {sportsResults.length > 0 && (
            <section className="flex-1 min-h-0 flex flex-col">
              <PageHeader
                size="section"
                title="Sports matches"
                subtitle={`${sportsResults.length} channel${
                  sportsResults.length === 1 ? '' : 's'
                }`}
              />
              <div className="flex-1 min-h-0">
                <ChannelGrid
                  channels={sportsResults}
                  onChannelClick={setCurrentChannel}
                  activeChannelId={currentChannel?.id}
                  columns={4}
                />
              </div>
            </section>
          )}

          {/* All-channels results (if loaded) */}
          {allChannels && allResults.length > 0 && (
            <section className="flex-1 min-h-0 flex flex-col">
              <PageHeader
                size="section"
                title="All channel matches"
                subtitle={`${allResults.length} channel${
                  allResults.length === 1 ? '' : 's'
                }`}
              />
              <div className="flex-1 min-h-0">
                <ChannelGrid
                  channels={allResults}
                  onChannelClick={setCurrentChannel}
                  activeChannelId={currentChannel?.id}
                  columns={4}
                />
              </div>
            </section>
          )}

          {/* No results at all */}
          {sportsResults.length === 0 && (!allChannels || allResults.length === 0) && (
            <div className="text-center py-12 text-text-muted">
              <p>No channels match "{query}"</p>
              {!allChannels && (
                <p className="text-xs mt-1 opacity-60">Try clicking &quot;Search all 10k+ channels&quot; above.</p>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}